import React, { useState } from 'react'
import { BGFields } from './PriceAndArchitecture';
import { useTheme } from '../../../middleware/ThemeContext';
import StyledText from '../../../components/StyledComponents/StyledText';
import FormikInputField from '../../../components/Formik/FormikInputField';
import styled from 'styled-components';
import { Theme } from '../../../utils/themes';
import FormikSelect from '../../../components/Formik/FormikSelect';
import { FormActions } from '../../../components/FormBuilder/CustomForm/FormFiller';
import { Button } from '../../../components/form/Button';
import CloseIcon from '../../../assets/icons/CloseIcon';
import { MembershipTypeFormValues } from '../../../types/types';
import { useGetCriteriaConditionsQuery, useGetColumnsByScopeAllPagesQuery } from '../../../api/searchQueryApiSlice';
import AMSSelect from '../../../components/AMSSelect';
import FormikDateRange from '../../../components/Formik/FormikDateRange';
import FormikNumberRange from '../../../components/Formik/FormikNumberRange';
import FormikMultiSelect from '../../../components/Formik/FormikMultiSelect';
import FormikCheckBox from '../../../components/Formik/FormikCheckBox';
import { SkeletonCell } from '../../../components/SkeletonCell';
import { getIconForDataType } from '../../../components/FormBuilder/FieldSettings/fieldSettingsHelper';
import { formatLabel } from '../../../utils/helper';

interface AddPriceTierProps {
    close: () => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    values: MembershipTypeFormValues;
}

type Column = {
  field: string;
  label: string;
  dataType: string;
  options?: any[];
  [key: string]: any;
};

type Condition = {
  name: string;
  friendlyName: string;
  description: string;
  valueType: string;
  expectedValueType: string[];
};

const generateId = () => new Date().getTime() + Math.random();

const AddPriceTier: React.FC<AddPriceTierProps> = ({ close, values, setFieldValue }) => {
    const { theme } = useTheme();

    const [tierName, setTierName] = useState<string>('');
    const [tierPrice, setTierPrice] = useState<number>(0);
    const [selectedColumn, setSelectedColumn] = useState<string>('');
    const [selectedCondition, setSelectedCondition] = useState<string>('');
    const [conditionValue, setConditionValue] = useState<any>('');

    const [errors, setErrors] = useState({
        tierName: "",
        tierPrice: "",
        selectedColumn: "",
        selectedCondition: "",
        conditionValue: "",
    });

    // Fetch columns and conditions from API
    const { data: columnsData, isLoading: columnsLoading } = useGetColumnsByScopeAllPagesQuery({ 
        scope: values.membershipAppliesTo
    });
    const { data: conditionOptions, isLoading: conditionsLoading } = useGetCriteriaConditionsQuery();

    const columns = columnsData || [];

    const getValidConditionsForColumn = (column: any, operators: any) => {
        if (!column?.dataType) return [];

        const baseOps = (() => {
            switch (column.dataType) {
                case "string":
                    return operators.filter(
                        (op: any) =>
                            op.valueType === "single" &&
                            op.expectedValueType.includes("string")
                    );
                case "number":
                    return operators.filter(
                        (op: any) =>
                            ["single", "array (length = 2)"].includes(op.valueType) &&
                            op.expectedValueType.includes("number")
                    );
                case "date":
                    return operators.filter(
                        (op: any) =>
                            op.expectedValueType.includes("date")
                    );
                case "boolean":
                    return operators.filter(
                        (op: any) =>
                            op.valueType === "single" &&
                            op.expectedValueType.includes("boolean")
                    );
                case "select":
                    return operators.filter(
                        (op: any) =>
                            op.valueType === "array" &&
                            (op.expectedValueType.includes("string") ||
                                op.expectedValueType.includes("number"))
                    );
                case "multi-select":
                    return operators.filter(
                        (op: any) =>
                            op.valueType === "array" &&
                            (op.expectedValueType.includes("string") ||
                                op.expectedValueType.includes("number"))
                    );
                default:
                    return [];
            }
        })();

        const nullOps = operators.filter(
            (op: any) => op.name === "isNull" || op.name === "isNotNull"
        );

        const combined = [...baseOps, ...nullOps];
        const unique = Array.from(
            new Map(combined.map((op) => [op.name, op])).values()
        );

        return unique;
    };

    const getValueFieldType = (
        column?: Column,
        condition?: Condition
    ): "text" | "select" | "date" | "dateRange" | "number" | "numberRange" | "checkBox" | "none" | "multi-select" => {
        if (!column || !condition) return "none";

        const { dataType } = column;
        const { valueType, expectedValueType } = condition;

        const includes = (types: string[]) =>
            types.some((t) => expectedValueType.includes(t));

        if (
            valueType === "single" &&
            dataType === "string" &&
            includes(["string", "number"])
        ) {
            return "text";
        }

        if (
            valueType === "single" &&
            dataType === "number" &&
            includes(["number"])
        ) {
            return "number";
        }

        if (
            valueType === "array (length = 2)" &&
            dataType === "number" &&
            includes(["number"])
        ) {
            return "numberRange";
        }

        if (
            (valueType === "array" || valueType === "single") &&
            (dataType === "select" || dataType === "multi-select") &&
            includes(["string", "number"])
        ) {
            return valueType === "array" ? "multi-select" : "select";
        }

        if (
            (valueType === "array" || valueType === "single") &&
            dataType === "date" &&
            includes(["string", "number", "date"])
        ) {
            return "date";
        }

        if (
            valueType === "array (length = 2)" &&
            dataType === "date" &&
            includes(["number", "date"])
        ) {
            return "dateRange";
        }

        if (
            valueType === "single" &&
            dataType === "boolean" &&
            includes(["boolean"])
        ) {
            return "checkBox";
        }

        return "none";
    };

    const selectedColumnObj = columns.find((col) => col.field === selectedColumn);
    const validConditions = getValidConditionsForColumn(selectedColumnObj, conditionOptions || []);
    const selectedConditionObj = (conditionOptions || []).find(
        (cond: any) => cond.name === selectedCondition
    );
    const valueFieldType = getValueFieldType(selectedColumnObj, selectedConditionObj);

    const validateForm = () => {
        const newErrors = {
            tierName: "",
            tierPrice: "",
            selectedColumn: "",
            selectedCondition: "",
            conditionValue: "",
        };

        if (!tierName.trim()) {
            newErrors.tierName = "Tier name is required";
        }

        if (!tierPrice || tierPrice <= 0) {
            newErrors.tierPrice = "Price must be greater than 0";
        }

        if (!selectedColumn) {
            newErrors.selectedColumn = "Please select a column";
        }

        if (!selectedCondition) {
            newErrors.selectedCondition = "Please select a condition";
        }

        if (
            valueFieldType !== "none" &&
            valueFieldType !== "checkBox" &&
            (
            conditionValue === "" ||
            conditionValue === null ||
            conditionValue === undefined
            )
        ) {
            newErrors.conditionValue = "Condition value is required";
        }

        setErrors(newErrors);

        return !Object.values(newErrors).some(Boolean);
    };

    const addPriceTier = () => {
        // if (!tierName || !tierPrice || tierPrice <= 0) {
        //     // console.log('Please fill tier name and price');
        //     setNameError("Please fill tier name and price");
        //     return;
        // }

        // if (!selectedColumn || !selectedCondition) {
        //     console.log('Please select column and condition');
        //     setConditionError("Please select column and condition");
        //     return;
        // }

        // if (valueFieldType !== "none" && valueFieldType !== "checkBox" && conditionValue === '') {
        //     console.log('Please fill condition value');
        //     return;
        // }

        const isValid = validateForm();

        if (!isValid) return;

        const newTier = {
            id: generateId(),
            tierName: tierName,
            tierPrice: tierPrice,
            eligibilityType: selectedColumn,
            operator: selectedCondition,
            eligibilityRole: String(conditionValue),
        };
        
        setFieldValue("specialPriceTier", [
            ...values.specialPriceTier,
            newTier
        ]);

        // Reset form
        setTierName("");
        setTierPrice(0);
        setSelectedColumn("");
        setSelectedCondition("");
        setConditionValue("");

        close();
    };

  return (
        <BGFields theme={theme} className='mt-4'>
            <div className='d-flex gap-3'>
                <div className='col mb-3'>
                    <StyledText
                        className="mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                    >
                        Tier Name
                    </StyledText>
                    <FormikInputField
                        // className='mb-3'
                        name="tierName"
                        placeholder="Enter tier name here"
                        value={tierName}
                        onChange={(e: any) => {
                            setTierName(e.target.value);
                            setErrors((prev) => ({
                                ...prev,
                                tierName: "",
                            }));
                        }}
                    />
                    <StyledText
                        fontSize='12px'
                        fontWeight={400}
                        textColor='red'
                    >
                        {errors.tierName}
                    </StyledText>
                </div>
                <div className='col mb-3'>
                    <StyledText
                        className="mb-2"
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                    >
                        Price
                    </StyledText>
                    <FormikInputField
                        // className='mb-3'
                        name="tierPrice"
                        type="number"
                        placeholder="Enter tier price here"
                        value={tierPrice || ''}
                        onChange={(e: any) => {
                            setTierPrice(Number(e.target.value));
                            setErrors((prev) => ({
                                ...prev,
                                tierPrice: "",
                            }));
                        }}
                    />
                    <StyledText
                        fontSize="12px"
                        fontWeight={400}
                        textColor="red"
                    >
                        {errors.tierPrice}
                    </StyledText>
                </div>
            </div>

            <BoxLightBlue theme={theme}>
                <StyledText
                    className="mb-3"
                    textColor={theme.CorporateTheme?.colors?.headerColor}
                    fontWeight={600}
                >
                    Eligibility Rule
                </StyledText>

                {columnsLoading || conditionsLoading ? (
                    <div className="d-flex align-items-center gap-1 px-2 mt-2">
                        {Array(3)
                            .fill(null)
                            .map((_, i) => (
                                <SkeletonCell
                                    key={i}
                                    style={{
                                        maxWidth: "100%",
                                        height: "36px",
                                        marginBottom: "10px",
                                    }}
                                />
                            ))}
                    </div>
                ) : (
                    <>
                    <div className='d-flex gap-2 mb-3' style={{ gridTemplateColumns: '1fr 1fr 1fr', display: 'grid' }}>
                        <div className='col-7'>
                            <AMSSelect
                                staticOptions={columns.map((col) => ({
                                    label: col?.label,
                                    value: col?.field,
                                    dataType: col?.dataType,
                                    accessKey: col?.field,
                                    id: col?.field,
                                    icon: getIconForDataType(col?.dataType)
                                })) ?? []}
                                value={(() => {
                                    const opts = (columns.map((col) => ({
                                        label: col?.label,
                                        value: col?.field,
                                        dataType: col?.dataType,
                                        accessKey: col?.field,
                                        id: col?.field,
                                    })) ?? []);
                                    return opts.find((opt: any) => String(opt.value) === String(selectedColumn)) ?? null;
                                })()}
                                onChange={(opt: any) => {
                                    const nextValue = opt?.value ? String(opt.value) : "";
                                    setSelectedColumn(nextValue);
                                    setSelectedCondition("");
                                    setConditionValue("");
                                    setErrors((prev) => ({
                                        ...prev,
                                        selectedColumn: "",
                                    }));
                                }}
                                placeholder="Select column here"
                                isSearchable
                                showDataType
                                includeAccessKey
                                menuPosition="fixed"
                            />
                        </div>

                        <div className='col'>
                            <FormikSelect
                                value={(conditionOptions ?? []).find((opt: any) => opt.name === selectedCondition)?.friendlyName || ''}
                                name="condition"
                                options={validConditions?.map((cond: any) => ({
                                    label: cond.friendlyName,
                                    value: cond.name,
                                })) ?? []}
                                placeholder="Select condition here"
                                onChange={(val: any) => {
                                    const nextVal = typeof val === "string" ? val : val?.value;
                                    setSelectedCondition(nextVal ?? "");
                                    setConditionValue("");
                                    setErrors((prev) => ({
                                        ...prev,
                                        selectedCondition: "",
                                    }));
                                }}
                            />
                        </div> 
                    </div>
                    <div>
                        {valueFieldType !== "none" && valueFieldType !== "checkBox" ? (
                            <div>
                                {["text", "number"].includes(valueFieldType) && (
                                    <FormikInputField
                                        type={valueFieldType === "number" ? "number" : "text"}
                                        name="conditionValue"
                                        placeholder={`Enter ${formatLabel(selectedColumnObj?.field)}`}
                                        value={conditionValue || ''}
                                        onChange={(e: any) => {
                                            setConditionValue(valueFieldType === "number" ? Number(e.target.value) : e.target.value)
                                            setErrors((prev) => ({
                                                ...prev,
                                                conditionValue: "",
                                            }));
                                        }}
                                        className="form-control w-100"
                                    />
                                )}
                                {["select", "multi-select"].includes(valueFieldType) && (
                                    <FormikMultiSelect
                                        count
                                        buttonStyle={{ width: "100%" }}
                                        name="conditionValue"
                                        options={
                                            selectedColumnObj?.options?.map((opt: any) => ({
                                                id: opt?.label,
                                                value: opt?.label,
                                                name: opt?.label,
                                            })) || []
                                        }
                                        placeholder="Select one or more"
                                        className="w-100"
                                    />
                                )}
                                {valueFieldType === "date" && (
                                    <FormikInputField
                                        type="date"
                                        name="conditionValue"
                                        value={conditionValue || ''}
                                        onChange={(e: any) => {
                                            setConditionValue(e.target.value)
                                            setErrors((prev) => ({
                                                ...prev,
                                                conditionValue: "",
                                            }));
                                        }}
                                        className="form-control w-100"
                                    />
                                )}
                                {valueFieldType === "dateRange" && (
                                    <FormikDateRange
                                        name="conditionValue"
                                        placeholderStart="From"
                                        placeholderEnd="To"
                                    />
                                )}
                                {valueFieldType === "numberRange" && (
                                    <FormikNumberRange
                                        name="conditionValue"
                                        placeholderFrom="From"
                                        placeholderTo="To"
                                    />
                                )}
                            </div>
                        ) : null}

                        {valueFieldType === "checkBox" && (
                            <div className="mt-2 pt-1">
                                <FormikCheckBox
                                    name="conditionValue"
                                    label={`${formatLabel(selectedColumnObj?.label)}`}
                                    size={18}
                                    borderColor={theme.CorporateTheme?.colors?.primary}
                                    bgColor={theme.CorporateTheme?.colors?.primary}
                                    checkColor="white"
                                    className="w-100"
                                />
                            </div>
                        )}
                        <StyledText
                            fontSize="12px"
                            fontWeight={400}
                            textColor="red"
                        >
                            {errors.conditionValue}
                        </StyledText>
                    </div>
                    </>
                )}
                <StyledText
                    fontSize='12px'
                    fontWeight={400}
                    textColor='red'
                >
                    {errors.selectedColumn || errors.selectedCondition}
                </StyledText>
            </BoxLightBlue>

            <FormActions>
                <Button 
                    theme={theme} 
                    type="button" 
                    $variant="cancel" 
                    onClick={close} 
                >
                    <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Close
                </Button>
                <Button 
                    theme={theme} 
                    type="button" 
                    $variant="customize" 
                    onClick={() => addPriceTier()} 
                >
                    Create Tier
                </Button>
            </FormActions>

        </BGFields>
    )
}

export default AddPriceTier

const BoxLightBlue = styled.div<{ theme?: Theme }>`
  border: 0.5px solid;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.primaryLightBG};
  border-color: #0556CB1A;
  padding: 25px;
  margin-bottom: 20px;
`;